"use client";

import { useEffect, useMemo, useState, type JSX } from "react";
import {
  buildBreadcrumbManager,
  type BreadcrumbValue,
  type FacetValue,
} from "@coveo/headless";
import { useTranslations } from "next-intl";
import { ClientOnly } from "../../../atoms/ClientOnly";
import Icon from "../../../atoms/Icon";
import { ICONS } from "../../../variables/icons";
import { useSearchEngine } from "../../../hooks/useSearchEngine";
import { type SearchBreadboxProps } from "./search-breadbox.props";

export const Default = (props: SearchBreadboxProps): JSX.Element => {
  console.log(
    "---->[SearchBreadbox:Default] Component Search Scope",
    props.params?.SearchScope,
  );

  const { page, params } = props;
  const isPageEditing = page.mode.isEditing;

  const id = params?.RenderingIdentifier;
  const searchEngine = useSearchEngine(params?.SearchScope);

  const t = useTranslations();
  const clearAllLabel = t.has("coveo-clear-all-filters")
    ? t("coveo-clear-all-filters")
    : "Clear all filters";

  // Build the manager once per engine. The Coveo headless API exposes a
  // subscribe/unsubscribe contract; we now properly tear it down on unmount.
  const breadcrumbManager = useMemo(
    () => buildBreadcrumbManager(searchEngine),
    [searchEngine],
  );
  const [breadcrumbsState, setBreadcrumbsState] = useState(
    breadcrumbManager.state,
  );

  useEffect(() => {
    const unsubscribe = breadcrumbManager.subscribe(() => {
      setBreadcrumbsState(breadcrumbManager.state);
    });
    return () => unsubscribe();
  }, [breadcrumbManager]);

  const allCrumbs: Array<BreadcrumbValue<FacetValue>> =
    breadcrumbsState.facetBreadcrumbs.flatMap(
      (facetCrumbs) => facetCrumbs.values,
    );

  const hasCrumbs = breadcrumbsState.facetBreadcrumbs.length > 0;

  return (
    <div
      className={`custom-breadcrumbs ${params?.styles ?? ""}`}
      id={id ?? undefined}
    >
      <ClientOnly>
        {isPageEditing ? (
          <div className="is-metadata-hint">
            <strong>Search:</strong> Search breadbox
          </div>
        ) : (
          hasCrumbs && (
            <div className="custom-breadcrumbs__container flex justify-between px-[var(--spacing-sixteen)] mb-5">
              <div className="flex flex-wrap">
                {allCrumbs.map((crumb, index) => (
                  <button
                    key={`${crumb.value.value}-${index}`}
                    type="button"
                    className="custom-breadcrumbs__pill flex items-center text-sm text-filter-blue cursor-pointer capitalize leading-5 hover:underline font-['Open_Sans'] ml-0 mr-3 mb-[5px] text-left items-baseline"
                    onClick={() => crumb.deselect()}
                    aria-label={crumb.value.value}
                  >
                    <span className="custom-breadcrumbs__pill-text">
                      {crumb.value.value}
                      <Icon
                        className="cursor-pointer w-[9px] h-[9px] min-w-[9px] ml-1 inline-block"
                        icon={ICONS.FILTER_CLOSE}
                      />
                    </span>
                  </button>
                ))}
              </div>
              <div>
                <button
                  type="button"
                  className="body2-link min-w-[120px] text-sm text-filter-blue cursor-pointer capitalize leading-5 hover:underline font-['Open_Sans'] ml-1 mr-[1px]"
                  onClick={() => breadcrumbManager.deselectAll()}
                >
                  {clearAllLabel}
                </button>
              </div>
            </div>
          )
        )}
      </ClientOnly>
    </div>
  );
};
